/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import "./index.scss";

import React from "react";
import { createRoot } from "react-dom/client";

import App from "./App";
import { Auth } from "./Auth";
import * as serviceWorker from "./serviceWorker";

if (!process.env.IMJS_AUTH_CLIENT_CLIENT_ID) {
  throw new Error(
    "Please add a valid OIDC client id to the .env file and restart the application. See the README for more information."
  );
}
if (!process.env.IMJS_AUTH_CLIENT_SCOPES) {
  throw new Error(
    "Please add valid scopes for your OIDC client to the .env file and restart the application. See the README for more information."
  );
}
if (!process.env.IMJS_AUTH_CLIENT_REDIRECT_URI) {
  throw new Error(
    "Please add a valid redirect URI to the .env file and restart the application. See the README for more information."
  );
}

function buildRuntimeAuthUrl(configuredUrl: string | undefined, fallbackPath: string): string {
  const trimmed = String(configuredUrl ?? "").trim();
  const currentOrigin = window.location.origin;

  if (!trimmed) {
    return new URL(fallbackPath, currentOrigin).toString();
  }

  try {
    const parsed = new URL(trimmed);
    return new URL(parsed.pathname || fallbackPath, currentOrigin).toString();
  } catch {
    return new URL(trimmed, currentOrigin).toString();
  }
}

const runtimeRedirectUri = buildRuntimeAuthUrl(
  process.env.IMJS_AUTH_CLIENT_REDIRECT_URI,
  "/signin-callback"
);
const runtimeLogoutUri = buildRuntimeAuthUrl(
  process.env.IMJS_AUTH_CLIENT_LOGOUT_URI,
  "/"
);

Auth.initialize({
  scope: process.env.IMJS_AUTH_CLIENT_SCOPES,
  clientId: process.env.IMJS_AUTH_CLIENT_CLIENT_ID,
  redirectUri: runtimeRedirectUri,
  postSignoutRedirectUri: runtimeLogoutUri,
  responseType: "code",
  authority: process.env.IMJS_AUTH_AUTHORITY,
});

const container = document.getElementById("root");
const root = createRoot(container!);

const redirectUrl = new URL(runtimeRedirectUri);
if (redirectUrl.pathname === window.location.pathname) {
  Auth.handleSigninCallback().catch(console.error);
} else {
  root.render(<App />);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
